/*
 * Farida Gupta - "Notify me when available" popup (theme/snippets/notify-me-modal.liquid).
 * On submit, the signup goes to whichever provider the form's data-provider names:
 * - "custom": pushed to CleverTap. This only pushes events to the `clevertap` global - it does
 *   not load the CleverTap SDK itself, that install snippet is expected to already be on the page.
 * - "doran": posted to the Doran Back In Stock app's storefront API (the same GraphQL mutation its
 *   own widget sends), so Doran mode keeps this popup's UI instead of the app's.
 */
const DORAN_API_URL = '/apps/bp_doran/api/graphql';
const DORAN_CREATE_NOTIFICATION = `
  mutation createBackInStockNotification($input: CreateBackInStockNotificationInput!) {
    createBackInStockNotification(input: $input) {
      success
      message
    }
  }
`;
function handleNotifyMeSubmit({ variantIds, email, countryCode, phoneNumber }) {
  // combine the two fields in the modal into E.164 format — CleverTap needs
  // "+" + country code + digits, no spaces (your number field shows "99999 99999")
  const formattedPhone = `${countryCode}${phoneNumber.replace(/\s+/g, '')}`;

  clevertap.onUserLogin.push({
    Site: {
      Identity: formattedPhone, // confirm this matches your account's identity config
      Phone: formattedPhone, // must be E.164, e.g. "+919999999999"
      Email: email,
      'MSG-email': true,
      'MSG-whatsapp': true // only set true if this counts as opt-in — see note below
    }
  });

  // One "Notify Me" event per selected size, same payload as the single-size flow had.
  variantIds.forEach((variantId) => {
    clevertap.event.push('Notify Me', {
      'Variant ID': String(variantId)
    });
  });
}

// Doran's mutation takes a single variant, so this is called once per selected size.
async function handleDoranNotifyMeSubmit({ form, sizeCheckbox, email, countryCode, phoneNumber }) {
  const digits = phoneNumber.replace(/\D/g, '');

  const response = await fetch(DORAN_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: DORAN_CREATE_NOTIFICATION,
      variables: {
        input: {
          language: window.$bpDoranInit?.language || document.documentElement.lang || 'en',
          productId: form.dataset.productId,
          productTitle: form.dataset.productTitle,
          variantId: String(sizeCheckbox.value),
          variantTitle: sizeCheckbox.dataset.variantTitle,
          email,
          // Doran's own widget sends the international format, e.g. "+91 99999 99999"
          phone: digits ? `${countryCode} ${digits.replace(/^(\d{5})(\d{5})$/, '$1 $2')}` : '',
          isOptIn: false,
          isAcceptsMarketing: false
        }
      }
    })
  });

  const payload = await response.json().catch(() => ({}));
  const result = payload.data?.createBackInStockNotification;

  if (!response.ok || payload.errors?.length || !result?.success) {
    throw new Error(result?.message || payload.errors?.[0]?.message || 'Something went wrong. Please try again.');
  }
}

function showNotifyMeBanner(form, message, type) {
  form.querySelector('.notify-me-modal__banner')?.remove();

  // Same "banner--success" confirmation pattern as add to cart (theme.js onVariantAdded_fn).
  const banner = document.createElement('div');
  banner.className = `banner banner--${type} notify-me-modal__banner`;
  banner.setAttribute('role', type === 'error' ? 'alert' : 'status');
  banner.setAttribute('aria-live', 'polite');
  banner.textContent = message;
  form.prepend(banner);
}

function setSizesError(sizes, message) {
  sizes.classList.toggle('is-invalid', Boolean(message));
  sizes.querySelector('.notify-me-modal__sizes-error')?.remove();

  if (message) {
    const error = document.createElement('p');
    error.className = 'notify-me-modal__sizes-error';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    sizes.append(error);
  }
}

// Keeps the dropdown's summary showing the ticked sizes ("S, M") or the placeholder.
function syncSizesSummary(sizes) {
  const selected = [...sizes.querySelectorAll('input[type="checkbox"]:checked')].map((checkbox) =>
    checkbox.nextElementSibling.textContent.trim()
  );

  sizes.querySelector('.notify-me-modal__sizes-value').textContent = selected.length
    ? selected.join(', ')
    : sizes.dataset.placeholder;

  if (selected.length) setSizesError(sizes, null);
}

document.addEventListener('change', (event) => {
  const sizes = event.target.closest('.notify-me-modal__sizes');
  if (sizes) syncSizesSummary(sizes);
});

// Close the size dropdown on a click outside it, or on Escape. The Escape listener sits on window
// in capture phase so it runs before the modal's focus trap (document, capture) and can stop the
// same key press from also closing the whole modal.
document.addEventListener('click', (event) => {
  document.querySelectorAll('.notify-me-modal__sizes[open]').forEach((sizes) => {
    if (!sizes.contains(event.target)) sizes.open = false;
  });
});

window.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Escape') return;
    const sizes = event.target.closest?.('.notify-me-modal__sizes[open]');
    if (!sizes) return;

    event.stopPropagation();
    sizes.open = false;
    sizes.querySelector('summary').focus();
  },
  true
);

document.addEventListener('input', (event) => {
  const field = event.target.closest('.notify-me-modal__form [name="notify_me[whatsapp]"]');
  if (!field) return;

  // Strip anything but digits as the customer types/pastes, so letters/symbols/spaces
  // can never end up in the WhatsApp number field.
  const cursorFromEnd = field.value.length - field.selectionEnd;
  field.value = field.value.replace(/\D/g, '');
  const cursor = field.value.length - cursorFromEnd;
  field.setSelectionRange(cursor, cursor);
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('.notify-me-modal__form');
  if (!form) return;

  event.preventDefault();

  // Checkbox groups have no native "required", so at least one size is enforced here.
  const sizes = form.querySelector('.notify-me-modal__sizes');
  const sizeCheckboxes = [...form.querySelectorAll('input[name="notify_me[size]"]:checked')];

  if (sizes && !sizeCheckboxes.length) {
    setSizesError(sizes, sizes.dataset.requiredMessage);
    sizes.open = true;
    sizes.querySelector('summary').focus();
    return;
  }

  if (sizes) sizes.open = false;

  const fields = {
    email: form.elements['notify_me[email]'].value,
    countryCode: form.elements['notify_me[country_code]'].value,
    phoneNumber: form.elements['notify_me[whatsapp]'].value
  };

  if (form.dataset.provider === 'doran') {
    const submitButton = form.querySelector('[type="submit"]');
    submitButton?.setAttribute('aria-busy', 'true');
    submitButton?.setAttribute('disabled', '');

    const results = await Promise.allSettled(
      sizeCheckboxes.map((sizeCheckbox) => handleDoranNotifyMeSubmit({ form, sizeCheckbox, ...fields }))
    );

    submitButton?.removeAttribute('aria-busy');
    submitButton?.removeAttribute('disabled');

    const failure = results.find((result) => result.status === 'rejected');

    if (failure) {
      // Untick the sizes that did go through, so a retry only resends the failed ones.
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') sizeCheckboxes[index].checked = false;
      });
      if (sizes) syncSizesSummary(sizes);

      showNotifyMeBanner(form, failure.reason.message, 'error');
      return;
    }
  } else {
    if (typeof clevertap === 'undefined') {
      console.warn('CleverTap is not loaded - "Notify me" signup was not tracked.');
      showNotifyMeBanner(form, 'Something went wrong. Please try again.', 'error');
      return;
    }

    handleNotifyMeSubmit({ variantIds: sizeCheckboxes.map((checkbox) => checkbox.value), ...fields });
  }

  // Stays until the customer closes the modal themselves - no auto-dismiss/auto-close.
  showNotifyMeBanner(form, form.dataset.successMessage, 'success');
  form.reset();
  if (sizes) syncSizesSummary(sizes);
});
